import { useQuery } from '@tanstack/react-query';
import { fetchAvailableHostsInAreaByDuration } from 'flux/clients/availability';
import { z } from 'zod';

const UseAvailableHostsByDurationParamsSchema = z.object({
  area: z.string(),
  time: z.string(),
  date: z.coerce.date(),
  duration: z.number().int().positive(),
  nrOfHostsToReturn: z.number().int().positive(),
});

type UseAvailableHostsByDurationParams = z.infer<
  typeof UseAvailableHostsByDurationParamsSchema
>;

const useAvailableHostsForAreaByDuration = ({
  area,
  time,
  duration,
  date,
  nrOfHostsToReturn,
}: UseAvailableHostsByDurationParams) => {
  const query = useQuery({
    queryKey: ['availableHostsForAreaByDuration', area, duration, date, time],
    queryFn: async () => {
      const response = await fetchAvailableHostsInAreaByDuration(
        area,
        date,
        time,
        duration * 60,
      );
      return {
        ...response,
        availableHosts:
          response?.availableHosts?.slice(0, nrOfHostsToReturn) || [],
      };
    },
    retry: 0,
    enabled: !!area && !!duration && !!date && !!time,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  return {
    isLoading: query.isLoading,
    availableHostIds: query.data?.availableHosts || [],
  };
};

export default useAvailableHostsForAreaByDuration;
